import {
	AfterViewInit,
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { TxApiService } from '../../../shared/services/txapi.service';
import { Subscription } from 'rxjs';
import {
	BarController,
	BarElement,
	CategoryScale,
	Chart,
	ChartDataset,
	Legend,
	LinearScale,
	LineController,
	LineElement,
	PointElement,
	Tooltip,
} from 'chart.js';
import { FormControl } from '@angular/forms';
import { ListOfWorkflowGroup } from '../../../shared/interfaces/workflow';
import { TranslateService } from '@ngx-translate/core';

// Register Chart.js components
Chart.register(
	BarController,
	LineController,
	BarElement,
	LineElement,
	PointElement,
	LinearScale,
	CategoryScale,
	Tooltip,
	Legend
);

interface TimeAccountSum {
	persnr: number;
	last_name: string;
	first_name: string;
	year: number;
	month: number;
	target_minutes: number;
	worked_minutes: number;
	difference_minutes: number;
	project_minutes: number;
	project_diff_minutes: number;
	AbsenceList: Array<{
		id: number;
		name: string;
		day_count: number;
		color: string;
	}>;
}

interface AbsenceType {
	id: number;
	name: string;
	totalDays: number;
	color: string;
}

// Interface for employee absences - now with hours instead of days for time tracking
interface EmployeeAbsence {
	persnr: number;
	fullName: string;
	absences: Map<number, {
		id: number;
		name: string;
		days: number;
		color: string;
	}>;
	target_hours: number;
	worked_hours: number;
	project_hours: number;
	difference_hours: number;
	project_diff_hours: number;
}

@Component({
	selector: 'tx-time-sums-chart-tl',
	templateUrl: './tx-time-sums-chart-tl.component.html',
	styleUrls: ['./tx-time-sums-chart-tl.component.scss'],
})
export class TxTimeSumsChartTlComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
	@Input() WfGroups: Array<ListOfWorkflowGroup> = null;
	@Input() CalendarData: any = null;
	@Input() groupFilterTrigger = false;
	@ViewChild('absenceChartCanvas') absenceChartCanvas: ElementRef;
	@ViewChild('chartCanvas') chartCanvas: ElementRef;

	timeRangeOptions: { value: number }[] = [
		{ value: 3 },
		{ value: 6 },
		{ value: 12 },
		{ value: 15 },
		{ value: -1 },
		{ value: -2 },
	];

	selectedTimeRange = new FormControl(null); // Set to null initially
	// Datumsfelder für den Von-Bis-Zeitraum
	selectedStartDate = new FormControl(new Date());
	selectedEndDate = new FormControl(new Date());

	timeAccountSums: TimeAccountSum[] = [];
	absenceTypes: AbsenceType[] = [];
	employeeAbsences: EmployeeAbsence[] = [];
	loading = false;
	chart: Chart;
	absenceChart: Chart;

	private subscriptions = new Subscription();
	private showProjectChart: boolean;

	constructor(private txApiService: TxApiService, private translate: TranslateService) {}

	ngOnInit(): void {
		this.txApiService.callAPI('getSetting', { settingName: 'SYS_BDE' }).subscribe(
			(response) => {
				this.showProjectChart = response.settingValue
			},
		);

		this.subscriptions.add(
			this.translate.onLangChange.subscribe(() => {
				if (this.chart) {
					this.createTimeTrackingChart();
				}
				if (this.absenceChart) {
					this.createStackedAbsenceChart();
				}
			})
		);

		this.selectedTimeRange.valueChanges.subscribe((value) => {
			if (value !== null) {
				this.fetchData();
			}
		});

		// Bei Änderung des Start- oder Enddatums Daten neu laden, wenn Zeitraummodus aktiv
		this.selectedStartDate.valueChanges.subscribe(() => {
			if (this.selectedTimeRange.value === -2) {
				this.fetchData();
			}
		});

		this.selectedEndDate.valueChanges.subscribe(() => {
			if (this.selectedTimeRange.value === -2) {
				this.fetchData();
			}
		});

		// No automatic data fetch on init
	}

	ngAfterViewInit(): void {
		// We'll create charts only when data is available
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['timeAccountSums'] && !changes['timeAccountSums'].firstChange) {
			if (this.absenceChart) {
				this.updateStackedAbsenceChart();
			}
			if (this.chart) {
				this.updateTimeTrackingChart();
			}
		}

		if ((changes['WfGroups'] && !changes['WfGroups'].firstChange) || 
			(changes['CalendarData'] && !changes['CalendarData'].firstChange) ||
			(changes['groupFilterTrigger'] && !changes['groupFilterTrigger'].firstChange)) {
			if (this.timeAccountSums.length > 0) {
				this.absenceTypes = this.calculateAllAbsenceTypes();
				this.employeeAbsences = this.calculateEmployeeAbsences();
				if (this.absenceChart) {
					this.updateStackedAbsenceChart();
				}
				if (this.chart) {
					this.updateTimeTrackingChart();
				}
			}
		}
	}

	ngOnDestroy(): void {
		if (this.chart) {
			this.chart.destroy();
		}
		if (this.absenceChart) {
			this.absenceChart.destroy();
		}
		this.subscriptions.unsubscribe();
	}



	fetchData(): void {
		// Only fetch data if a time range is selected
		if (this.selectedTimeRange.value === null) {
			return;
		}

		this.loading = true;
		let endDate = new Date();
		let startDate = new Date();
		let outputMode = '';

		if (this.selectedTimeRange.value === -2) {
			// Zeitraum-Modus: Von-Bis-Datumsauswahl verwenden
			if (this.selectedStartDate.value && this.selectedEndDate.value) {
				startDate = new Date(this.selectedStartDate.value);
				endDate = new Date(this.selectedEndDate.value);
			}
			outputMode = 'day';
		} else if (this.selectedTimeRange.value === -1) {
			const lastYear = endDate.getFullYear() - 1;
			startDate = new Date(lastYear, 0, 1);
			endDate.setFullYear(lastYear);
			endDate.setMonth(11);
			endDate.setDate(31);
		} else {
			// Setze das Enddatum auf den letzten Tag des vorherigen Monats
			endDate.setDate(0); // Setzt auf den letzten Tag des Vormonats
			// Starte beim Ende des vorherigen Monats und gehe die gewählte Anzahl von Monaten zurück
			startDate = new Date(endDate);
			startDate.setMonth(endDate.getMonth() - (this.selectedTimeRange.value - 1));
			startDate.setDate(1); // Setzt auf den ersten Tag des Startmonats
		}

		const request: any = {
			von: this.formatDate(startDate),
			bis: this.formatDate(endDate),
			teamleader: 1
		};

		if (outputMode) {
			request.output_mode = outputMode;
		}

		this.subscriptions.add(
			this.txApiService.callAPI('getDashboardTimeAccSums', request).subscribe(
				(response) => {
					if (response && response.TimeAccountSumList) {
						this.timeAccountSums = response.TimeAccountSumList;
						this.absenceTypes = this.calculateAllAbsenceTypes();
						this.employeeAbsences = this.calculateEmployeeAbsences();
						this.createStackedAbsenceChart();
						this.createTimeTrackingChart();
					}
					this.loading = false;
				},
				(error) => {
					console.error('Error fetching time account data:', error);
					this.loading = false;
				}
			)
		);
	}

	private formatDate(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}T00:00:00`;
	}

	// Method for calculating all absence types that exist in the system
	private calculateAllAbsenceTypes(): AbsenceType[] {
		const absenceTypesMap = new Map<number, AbsenceType>();

		const filteredTimeAccountSums = this.filterByWorkflowGroups(this.timeAccountSums);

		filteredTimeAccountSums.forEach((monthData) => {
			if (monthData.AbsenceList) {
				monthData.AbsenceList.forEach((absence) => {
					// Exclude target days and actual days
					const isSollTage = absence.name.includes('Soll-Tage');
					const isIstTage = absence.name.includes('Ist-Tage');

					if (!isSollTage && !isIstTage && !absenceTypesMap.has(absence.id)) {
						absenceTypesMap.set(absence.id, {
							id: absence.id,
							name: absence.name,
							totalDays: 0,
							color: absence.color,
						});
					}

					const existingType = absenceTypesMap.get(absence.id);
					if (existingType && !isSollTage && !isIstTage) {
						existingType.totalDays += absence.day_count;
					}
				});
			}
		});

		return Array.from(absenceTypesMap.values());
	}

	private filterByWorkflowGroups(data: TimeAccountSum[]): TimeAccountSum[] {
		if (!this.WfGroups || this.WfGroups.length === 0) {
			return data;
		}

		const activeGroups = this.WfGroups.filter(group => group.active === true);
		const activeGroupIds = activeGroups.map(group => group.id);

		if (activeGroupIds.length === 0) {
			return [];
		}

		// Get allowed personalnr from calendar data
		let allowedPersonalNrs: number[] = [];

		if (this.CalendarData && Array.isArray(this.CalendarData)) {
			// Filter calendar data by active workflow groups
			allowedPersonalNrs = this.CalendarData
				.filter(person => activeGroupIds.includes(person.workflow_group))
				.map(person => person.personalnr);
		} else {
			// Fallback: return all data if no calendar data available
			return data;
		}

		// Filter time account data by allowed personalnr
		return data.filter(item => allowedPersonalNrs.includes(item.persnr));
	}

	private minutesToHours(minutes: number): number {
		return parseFloat((minutes / 60).toFixed(1));
	}


	private calculateEmployeeAbsences(): EmployeeAbsence[] {
		const employeeMap = new Map<number, EmployeeAbsence>();
		const isDayView = this.selectedTimeRange.value === -2;

		const filteredTimeAccountSums = this.filterByWorkflowGroups(this.timeAccountSums);

		filteredTimeAccountSums.forEach((data) => {
			if (!employeeMap.has(data.persnr)) {
				employeeMap.set(data.persnr, {
					persnr: data.persnr,
					fullName: `${data.last_name}, ${data.first_name}`,
					absences: new Map(),
					target_hours: 0,
					worked_hours: 0,
					project_hours: 0,
					difference_hours: 0,
					project_diff_hours: 0
				});
			}

			const employee = employeeMap.get(data.persnr);

			if (employee) {
				employee.target_hours += this.minutesToHours(data.target_minutes);
				employee.worked_hours += this.minutesToHours(data.worked_minutes);
				employee.project_hours += this.minutesToHours(data.project_minutes);
				employee.difference_hours += this.minutesToHours(data.difference_minutes);
				employee.project_diff_hours += this.minutesToHours(data.project_diff_minutes);
			}

			// Rest der Funktion bleibt unverändert...
			if (data.AbsenceList && employee) {
				data.AbsenceList.forEach((absence) => {
					const isSollTage = absence.name.includes('Soll-Tage');
					const isIstTage = absence.name.includes('Ist-Tage');

					if (!isSollTage && !isIstTage) {
						const absenceDays = absence.day_count;

						if (!employee.absences.has(absence.id)) {
							employee.absences.set(absence.id, {
								id: absence.id,
								name: absence.name,
								days: 0,
								color: absence.color
							});
						}

						const absenceDetail = employee.absences.get(absence.id);
						if (absenceDetail) {
							absenceDetail.days += absenceDays;
						}
					}
				});
			}
		});

		return (
			Array.from(employeeMap.values())
				.filter((employee) => {
					if (isDayView) {
						return true;
					}

					const hasAbsences = Array.from(employee.absences.values()).some((absence) => absence.days > 0.1);

					const hasTimeData =
						employee.target_hours > 0.1 || employee.worked_hours > 0.1 || employee.project_hours > 0.1;

					return hasAbsences || hasTimeData;
				})
				.sort((a, b) => {
					const lastNameA = a.fullName.split(',')[0].trim();
					const lastNameB = b.fullName.split(',')[0].trim();

					return lastNameA.localeCompare(lastNameB);
				})
		);
	}

	private getCssVariable(variableName: string): string {
		const computedStyle = getComputedStyle(this.chartCanvas.nativeElement);
		return computedStyle.getPropertyValue(variableName).trim();
	}

	private createTimeTrackingChart(): void {
		if (!this.chartCanvas) {
			return;
		}

		if (this.chart) {
			this.chart.destroy();
		}

		const ctx = this.chartCanvas.nativeElement.getContext('2d');
		const employeeData = this.employeeAbsences;

		const datasets: ChartDataset<'bar' | 'line', (number | null)[]>[] = [];

		const targetBg = this.getCssVariable('--tx-target-time-bg');
		const targetBorder = this.getCssVariable('--tx-target-time-border');

		const differenceBorder = this.getCssVariable('--tx-difference-time-border');

		const workedBg = this.getCssVariable('--tx-worked-time-bg');
		const workedBorder = this.getCssVariable('--tx-worked-time-border');

		const projectDiffBorder = this.getCssVariable('--tx-project-diff-time-border');

		const projectBg = this.getCssVariable('--tx-project-time-bg');
		const projectBorder = this.getCssVariable('--tx-project-time-border');

		const sollLabel = this.translate.instant('txZeitKonto.chartLegendSoll');
		const istLabel = this.translate.instant('txZeitKonto.chartLegendIst');
		const diffLabel = this.translate.instant('txZeitKonto.chartLegendDiff');
		const projectLabel = this.translate.instant('txZeitKonto.chartLegendProject');
		const projectDiffLabel = this.translate.instant('txZeitKonto.chartLegendProjectDiff');

		// Balken-Datasets
		const targetDataset = {
			type: 'bar' as const,
			label: sollLabel,
			data: employeeData.map(employee => employee.target_hours > 0 ? employee.target_hours : null),
			backgroundColor: targetBg,
			borderColor: targetBorder,
			borderWidth: 1,
			barPercentage: 0.9,
			categoryPercentage: 0.9,
			stack: 'Stack 1',
			order: 3, // Höhere Zahl = weiter hinten im Zeichenablauf
			legendOrder: 1 // Niedrigere Zahl = weiter vorne in der Legende
		};
		Object.defineProperty(targetDataset, 'skipNull', { value: true });
		datasets.push(targetDataset);

		const workedDataset = {
			type: 'bar' as const,
			label: istLabel,
			data: employeeData.map(employee => employee.worked_hours > 0 ? employee.worked_hours : null),
			backgroundColor: workedBg,
			borderColor: workedBorder,
			borderWidth: 1,
			barPercentage: 0.9,
			categoryPercentage: 0.9,
			stack: 'Stack 2',
			order: 4, // Höhere Zahl = weiter hinten im Zeichenablauf
			legendOrder: 3 // Niedrigere Zahl = weiter vorne in der Legende
		};
		Object.defineProperty(workedDataset, 'skipNull', { value: true });
		datasets.push(workedDataset);

		if (this.showProjectChart){
			const projectDataset = {
				type: 'bar' as const,
				label: projectLabel,
				data: employeeData.map(employee => employee.project_hours > 0 ? employee.project_hours : null),
				backgroundColor: projectBg,
				borderColor: projectBorder,
				borderWidth: 1,
				barPercentage: 0.9,
				categoryPercentage: 0.9,
				stack: 'Stack 3',
				order: 5, // Höhere Zahl = weiter hinten im Zeichenablauf
				legendOrder: 5 // Niedrigere Zahl = weiter vorne in der Legende
			};
			Object.defineProperty(projectDataset, 'skipNull', { value: true });
			datasets.push(projectDataset);
		}

		// Linien-Datasets
		const differenceDataset = {
			type: 'line' as const,
			label: diffLabel,
			data: employeeData.map(employee => employee.difference_hours),
			backgroundColor: differenceBorder,
			borderColor: differenceBorder,
			borderWidth: 2,
			tension: 0.3,
			fill: false,
			spanGaps: true,
			order: 1, // Niedrigere Zahl = weiter vorne im Zeichenablauf
			legendOrder: 2 // Niedrigere Zahl = weiter vorne in der Legende
		};
		Object.defineProperty(differenceDataset, 'skipNull', { value: true });
		datasets.push(differenceDataset);

		if (this.showProjectChart) {
			const projectDiffDataset = {
				type: 'line' as const,
				label: projectDiffLabel,
				data: employeeData.map(employee => employee.project_diff_hours),
				backgroundColor: projectDiffBorder,
				borderColor: projectDiffBorder,
				borderWidth: 2,
				tension: 0.3,
				fill: false,
				spanGaps: true,
				order: 2, // Niedrigere Zahl = weiter vorne im Zeichenablauf
				legendOrder: 4 // Niedrigere Zahl = weiter vorne in der Legende
			};
			Object.defineProperty(projectDiffDataset, 'skipNull', { value: true });
			datasets.push(projectDiffDataset);
		}

		const unitHours = this.translate.instant('txTimeSumsChart.unitHours');
		let timeUnitLabel: string = unitHours;
		let xAxisTitle: string = unitHours;

		if (this.selectedTimeRange.value === -2) {
			const dateStr = (this.selectedStartDate.value && this.selectedEndDate.value) ?
				this.translate.instant('txTimeSumsChart.dateRangeFromTo', {
					start: this.selectedStartDate.value.toLocaleDateString(),
					end: this.selectedEndDate.value.toLocaleDateString(),
				}) :
				new Date().toLocaleDateString();

			timeUnitLabel = this.translate.instant('txTimeSumsChart.hoursInRange', { range: dateStr });
			xAxisTitle = this.translate.instant('txTimeSumsChart.hoursAt', { date: dateStr });
		}

		const chartOptions = {
			responsive: true,
			maintainAspectRatio: false,
			indexAxis: 'y' as const,
			scales: {
				x: {
					beginAtZero: true,
					stacked: false,
					title: { display: true, text: xAxisTitle },
					grid: {
						lineWidth: function(context) {
							if (context.tick.value === 0) {
								return 2;
							}
							return 1;
						},
						color: function(context) {
							if (context.tick.value === 0) {
								return 'rgba(0, 0, 0, 0.8)';
							}
							return 'rgba(0, 0, 0, 0.1)';
						},
					},
				},
				y: {
					stacked: false,
					title: { display: true, text: this.translate.instant('txTimeSumsChart.axisEmployee') },
				}
			},
			plugins: {
				tooltip: {
					callbacks: {
						label: (context) => {
							const label = context.dataset.label || '';
							const value = parseFloat(context.parsed.x.toFixed(1));
							return `${label}: ${value} ${unitHours}`;
						},
					},
				},
				legend: {
					display: true,
					position: 'top' as const,
					labels: {
						sort: (a, b) => {
							// Verwende die benutzerdefinierte legendOrder-Eigenschaft
							// @ts-ignore
							return (a.datasetIndex !== undefined && b.datasetIndex !== undefined) ?
								// @ts-ignore
								(datasets[a.datasetIndex].legendOrder || 0) - (datasets[b.datasetIndex].legendOrder || 0) : 0;
						}
					}
				}
			},
		};

		this.chart = new Chart(ctx, {
			type: 'bar',
			data: {
				labels: employeeData.map(item => item.fullName),
				datasets: datasets,
			},
			options: chartOptions
		});
	}

	// Update-Methode für den Zeiten-Chart in Stunden
	private updateTimeTrackingChart(): void {
		if (!this.chart) {
			this.createTimeTrackingChart();
			return;
		}

		const employeeData = [...this.employeeAbsences];

		this.chart.data.labels = employeeData.map(item => item.fullName);

		// WICHTIG: Die Reihenfolge muss an die neue Datensatz-Reihenfolge angepasst werden

		// Dataset 0: Target hours (Bar)
		this.chart.data.datasets[0].data = employeeData.map(employee =>
			employee.target_hours > 0 ? employee.target_hours : null
		);

		// Dataset 1: Worked hours (Bar)
		this.chart.data.datasets[1].data = employeeData.map(employee =>
			employee.worked_hours > 0 ? employee.worked_hours : null
		);

		if (this.showProjectChart){
			// Dataset 2: Project hours (Bar)
			this.chart.data.datasets[2].data = employeeData.map(employee =>
				employee.project_hours > 0 ? employee.project_hours : null
			);
		}

		// Dataset 3: Difference hours (Linie)
		this.chart.data.datasets[3].data = employeeData.map(employee =>
			employee.difference_hours !== 0 ? employee.difference_hours : null
		);

		if (this.showProjectChart) {
			// Dataset 4: Project diff hours (Linie)
			this.chart.data.datasets[4].data = employeeData.map(employee =>
				employee.project_diff_hours !== 0 ? employee.project_diff_hours : null
			);
		}

		// Prüfen, ob sich der Zeitraum geändert hat
		const isDayView = this.selectedTimeRange.value === -2;
		const currentTitle = this.getTimeChartTitle();

		const unitHours = this.translate.instant('txTimeSumsChart.unitHours');
		let newTitle: string = unitHours;
		if (isDayView) {
			const dateStr = (this.selectedStartDate.value && this.selectedEndDate.value) ?
				this.translate.instant('txTimeSumsChart.dateRangeFromTo', {
					start: this.formatDisplayDate(this.selectedStartDate.value),
					end: this.formatDisplayDate(this.selectedEndDate.value),
				}) :
				this.formatDisplayDate(new Date());

			newTitle = this.translate.instant('txTimeSumsChart.hoursAt', { date: dateStr });
		}

		// Nur wenn sich der Titel geändert hat, Chart neu erstellen
		if (currentTitle !== newTitle) {
			// Chart-Daten speichern
			const chartData = this.chart.data;
			const datasets = chartData.datasets;

			// Chart zerstören
			this.chart.destroy();

			// Neu erstellen mit aktualisierten Optionen
			const ctx = this.chartCanvas.nativeElement.getContext('2d');

			const chartOptions = {
				responsive: true,
				maintainAspectRatio: false,
				indexAxis: 'y' as const,
				scales: {
					x: {
						beginAtZero: true,
						stacked: false,
						title: { display: true, text: newTitle },
						grid: {
							lineWidth: function(context) {
								if (context.tick.value === 0) {
									return 2;
								}
								return 1;
							},
							color: function(context) {
								if (context.tick.value === 0) {
									return 'rgba(0, 0, 0, 0.8)';
								}
								return 'rgba(0, 0, 0, 0.1)';
							},
						},
					},
					y: {
						stacked: false,
						title: { display: true, text: this.translate.instant('txTimeSumsChart.axisEmployee') },
						ticks: {
							callback: function (val: any, index: string | number) {
								const allEmpty = chartData.datasets.every((dataset) => {
									return !dataset.data[index] || dataset.data[index] === 0;
								});

								return allEmpty ? null : this.getLabelForValue(val);
							},
						},
					},
				},
				plugins: {
					tooltip: {
						callbacks: {
							label: (context) => {
								const label = context.dataset.label || '';
								const value = parseFloat(context.parsed.x.toFixed(1));
								return `${label}: ${value} ${unitHours}`;
							},
						},
					},
					legend: {
						display: true,
						position: 'top' as const,
						labels: {
							sort: (a, b) => {
								// Verwende die benutzerdefinierte legendOrder-Eigenschaft
								// @ts-ignore
								return (a.datasetIndex !== undefined && b.datasetIndex !== undefined) ?
									// @ts-ignore
									(datasets[a.datasetIndex].legendOrder || 0) - (datasets[b.datasetIndex].legendOrder || 0) : 0;
							}
						}
					},
				},
			};

			this.chart = new Chart(ctx, {
				type: 'bar',
				data: chartData,
				options: chartOptions
			});
		} else {
			// Nur die Daten aktualisieren
			this.chart.update();
		}
	}

// Hilfsmethode, um den aktuellen Titel des TimeCharts zu ermitteln
	private getTimeChartTitle(): string {
		try {
			// Versuche, den Titel als Zeichenkette zu extrahieren
			// @ts-ignore - Ignoriere TypeScript-Fehler, da wir wissen, dass diese Struktur existiert
			return this.chart.options.scales.x.title.text || '';
		} catch (e) {
			// Wenn der Zugriff fehlschlägt, gib einen Standardwert zurück
			return '';
		}
	}

// Hilfsmethode zum Formatieren des Datums im deutschen Format
	private formatDisplayDate(date: Date): string {
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}.${month}.${year}`;
	}

	// Method for creating the stacked absence chart
	private createStackedAbsenceChart(): void {
		if (!this.absenceChartCanvas) {
			return;
		}

		if (this.absenceChart) {
			this.absenceChart.destroy();
		}

		const ctx = this.absenceChartCanvas.nativeElement.getContext('2d');
		const employeeData = this.employeeAbsences;
		const absenceTypes = this.absenceTypes;
		const isDayView = this.selectedTimeRange.value === -2;
		const absenceTitle = isDayView
			? this.translate.instant('txTimeSumsChart.axisDayAbsences')
			: this.translate.instant('txTimeSumsChart.axisDays');
		const unitDays = this.translate.instant('txTimeSumsChart.unitDays');
		const axisEmployee = this.translate.instant('txTimeSumsChart.axisEmployee');

		const filteredEmployeeData = employeeData.filter(employee => {
			return Array.from(employee.absences.values()).some((absence) => absence.days > 0.1);
		});

		// Create datasets for the stacked chart (one dataset per absence type)
		const datasets: ChartDataset<'bar', (number | null)[]>[] = absenceTypes.map(absenceType => {
			const dataset = {
				type: 'bar' as const,
				label: absenceType.name,
				data: filteredEmployeeData.map(employee => {
					const absenceDetail = employee.absences.get(absenceType.id);
					// Use null instead of 0 for empty values
					return absenceDetail && absenceDetail.days > 0 ? absenceDetail.days : null;
				}),
				backgroundColor: absenceType.color + '80',
				borderColor: absenceType.color,
				borderWidth: 1,
				stack: 'Absences',
			};
			// Add skipNull property separately after object creation
			Object.defineProperty(dataset, 'skipNull', { value: true });
			return dataset;
		});

		// Define chart options as a separate object
		const chartOptions = {
			responsive: true,
			maintainAspectRatio: false,
			indexAxis: 'y' as const,
			scales: {
				x: {
					beginAtZero: true,
					stacked: true,
					title: { display: true, text: absenceTitle },
				},
				y: {
					stacked: true,
					title: { display: true, text: axisEmployee },
					ticks: {
						callback: function (val: any, index: string | number) {
							// Skip this tick/label if all datasets have zero/null value at this index
							const allEmpty = datasets.every((dataset) => {
								return !dataset.data[index] || dataset.data[index] === 0;
							});

							// Return null to skip, or the label to show
							return allEmpty ? null : this.getLabelForValue(val);
						},
					},
				},
			},
			plugins: {
				tooltip: {
					callbacks: {
						label: (context) => {
							const label = context.dataset.label || '';
							const value = parseFloat(context.parsed.x.toFixed(1));
							return `${label}: ${value} ${unitDays}`;
						},
					},
				},
				legend: {
					display: true,
					position: 'top' as const,
				},
			},
		};

		this.absenceChart = new Chart(ctx, {
			type: 'bar',
			data: {
				labels: filteredEmployeeData.map(item => item.fullName),
				datasets: datasets,
			},
			options: chartOptions
		});
	}

	// Update method for the stacked absence chart
	private updateStackedAbsenceChart(): void {
		if (!this.absenceChart) {
			this.createStackedAbsenceChart();
			return;
		}

		this.absenceTypes = this.calculateAllAbsenceTypes();
		this.employeeAbsences = this.calculateEmployeeAbsences();

		const employeeData = [...this.employeeAbsences];
		const absenceTypes = [...this.absenceTypes];

		const filteredEmployeeData = employeeData.filter(employee => {
			return Array.from(employee.absences.values()).some((absence) => absence.days > 0.1);
		});

		// Update labels for employees
		this.absenceChart.data.labels = filteredEmployeeData.map(item => item.fullName);

		// Update datasets
		this.absenceChart.data.datasets = absenceTypes.map(absenceType => {
			const dataset = {
				type: 'bar' as const,
				label: absenceType.name,
				data: filteredEmployeeData.map(employee => {
					const absenceDetail = employee.absences.get(absenceType.id);
					return absenceDetail && absenceDetail.days > 0 ? absenceDetail.days : null;
				}),
				backgroundColor: absenceType.color + '80',
				borderColor: absenceType.color,
				borderWidth: 1,
				stack: 'Absences',
			};
			Object.defineProperty(dataset, 'skipNull', { value: true });
			Object.defineProperty(dataset, 'barPercentage', { value: 0.9 });
			Object.defineProperty(dataset, 'categoryPercentage', { value: 0.9 });
			return dataset;
		});

		// Statt direkt auf properties zuzugreifen, erstellen wir das Chart neu
		// wenn der Anzeigemodus geändert wurde
		const isDayView = this.selectedTimeRange.value === -2;
		const currentTitle = this.getAbsenceChartTitle();
		const newTitle = isDayView
			? this.translate.instant('txTimeSumsChart.axisDayAbsences')
			: this.translate.instant('txTimeSumsChart.axisDays');
		const unitDays = this.translate.instant('txTimeSumsChart.unitDays');
		const axisEmployee = this.translate.instant('txTimeSumsChart.axisEmployee');

		// Nur wenn sich der Titel geändert hat, Chart neu erstellen
		if (currentTitle !== newTitle) {
			// Chart-Daten speichern
			const chartData = this.absenceChart.data;

			// Chart zerstören
			this.absenceChart.destroy();

			// Neu erstellen mit aktualisierten Optionen
			const ctx = this.absenceChartCanvas.nativeElement.getContext('2d');

			const chartOptions = {
				responsive: true,
				maintainAspectRatio: false,
				indexAxis: 'y' as const,
				scales: {
					x: {
						beginAtZero: true,
						stacked: true,
						title: { display: true, text: newTitle },
					},
					y: {
						stacked: true,
						title: { display: true, text: axisEmployee },
						ticks: {
							callback: function (val: any, index: string | number) {
								// Skip this tick/label if all datasets have zero/null value at this index
								const allEmpty = chartData.datasets.every((dataset) => {
									return !dataset.data[index] || dataset.data[index] === 0;
								});

								// Return null to skip, or the label to show
								return allEmpty ? null : this.getLabelForValue(val);
							},
						},
					},
				},
				plugins: {
					tooltip: {
						callbacks: {
							label: (context) => {
								const label = context.dataset.label || '';
								const value = parseFloat(context.parsed.x.toFixed(1));
								return `${label}: ${value} ${unitDays}`;
							},
						},
					},
					legend: {
						display: true,
						position: 'top' as const,
					},
				},
			};

			this.absenceChart = new Chart(ctx, {
				type: 'bar',
				data: chartData,
				options: chartOptions
			});
		} else {
			// Nur die Daten aktualisieren
			this.absenceChart.update();
		}
	}

// Hilfsmethode, um den aktuellen Titel des AbsenceCharts zu ermitteln
	private getAbsenceChartTitle(): string {
		try {
			// Versuche, den Titel als Zeichenkette zu extrahieren
			// @ts-ignore - Ignoriere TypeScript-Fehler, da wir wissen, dass diese Struktur existiert
			return this.absenceChart.options.scales.x.title.text || '';
		} catch (e) {
			// Wenn der Zugriff fehlschlägt, gib einen Standardwert zurück
			return '';
		}
	}
}
